import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, concat, forkJoin, of } from 'rxjs';
import { last, map } from 'rxjs/operators';
import { API_BASE_URL_SERVICE } from '../../kiene-core.config';
import { KieneBackendApiService } from '../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../services/message-service/message.service';
import { ConfirmDialogInput, ConfirmDialogResult } from '../dialogs/confirm-dialog/confirm-dialog';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { Gruppe } from '../gruppen/classes/gruppe';
import {
    buildGruppeAuswaehlenDialogConfig,
    GruppeAuswaehlenDialogInput,
    GruppeAuswaehlenDialogResult,
} from '../gruppen/gruppe-auswaehlen-dialog/gruppe-auswaehlen-dialog';
import { AppService } from '../../services/app/app.service';
import { GruppeAuswaehlenDialogComponent } from '../gruppen/gruppe-auswaehlen-dialog/gruppe-auswaehlen-dialog.component';
import { GruppenService } from '../gruppen/gruppen.service';
import { GruppeTier } from './classes/gruppe-tier';
import { Tierbemerkung } from './classes/tierbemerkung';
import { Tierreklamation } from './classes/tierreklamation';
import { Router } from '@angular/router';
import { GruppeTierAussendienstDialogComponent } from './gruppe-tier-aussendienst-dialog/gruppe-tier-aussendienst-dialog.component';
import { GruppeTierAussendienstDialogInput } from './gruppe-tier-aussendienst-dialog/gruppe-tier-aussendienst-dialog';
import { Betriebskontakt } from '../stammdaten/betriebskontakte/betriebskontakt';
import { KieneUtils } from '../../utils/KieneUtils';

export class SelektierteTiereUpdateGruppeInput {
    gruppe_id_neu: number;
    gruppe_id_alt: number;
    gt_ids: number[] = [];
}

/** Optional Letzte-Aktionen context when moving animals between groups. */
export interface GruppeAendernDashboardSync {
    gda_id?: number;
    kunde_id?: number;
    tierliste_id?: number;
    einstall_tierliste_id?: number;
}

@Injectable({ providedIn: 'root' })
export class GruppeTierService {
    constructor(
        private api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private dialog: MatDialog,
        private messageService: MessageService,
        private gruppenService: GruppenService,
        private router: Router,
        private appService: AppService,
    ) {}

    ladeTier(gt_id: number) {
        return this.api.getByIdAlternative(
            `${this.apiBaseUrl}gruppe_tier/read_one_kundenportal.php`,
            'gt_id',
            gt_id.toString(),
        );
    }

    todMelden(gruppeTierId: number, kp_todesdatum: Date, entsorgerId: number, todesart_id: number) {
        const gt = new GruppeTier();
        gt.gt_id = gruppeTierId;
        gt.kp_todesdatum = kp_todesdatum;
        gt.entsorger_id = entsorgerId;
        gt.todesart_id = todesart_id;
        return this.api.post(`${this.apiBaseUrl}gruppe_tier/tod_kundenportal.php`, gt, null);
    }

    deleteTier(tier: GruppeTier): Observable<any> {
        return this.api.deleteGeneric(this.apiBaseUrl + 'gruppe_tier/delete_kundenportal.php', tier);
    }

    updateTierreklamation(tierreklamation: Tierreklamation): Observable<any> {
        return this.api.updateGeneric(`${this.apiBaseUrl}tierreklamation/update_kundenportal.php`, tierreklamation);
    }

    einstalldatumHitAendern(tiere: GruppeTier[], einstalldatum_neu: Date) {
        return this.api.post(
            this.apiBaseUrl + 'gruppe_tier/einstalldatum_aendern.php',
            { einstalldatum_neu: einstalldatum_neu, gruppen_tiere: tiere },
            null,
        );
    }

    abgangsdatumHitAendern(tiere: GruppeTier[], abgangsdatum_neu: Date) {
        return this.api.post(
            this.apiBaseUrl + 'gruppe_tier/abgangsdatum_aendern.php',
            { abgangsdatum_neu: abgangsdatum_neu, gruppen_tiere: tiere },
            null,
        );
    }

    update(gruppeTier: GruppeTier) {
        return this.api.updateGeneric(`${this.apiBaseUrl}gruppe_tier/update_kundenportal.php`, gruppeTier);
    }

    /**
     * Schnelle Aktualisierung nur der Händler-IDs (Performance-optimiert)
     */
    updateHaendler(gruppeTier: GruppeTier) {
        return this.api.updateGeneric(`${this.apiBaseUrl}gruppe_tier/update_haendler.php`, gruppeTier);
    }

    updateHerkunft(gruppeTier: GruppeTier) {
        return this.api.updateGeneric(`${this.apiBaseUrl}gruppe_tier/update_herkunft.php`, gruppeTier);
    }

    /** Per-animal herkunft only (gruppe_tier/update_herkunft.php), no tierliste side effects. */
    updateHerkunftForTiere(gruppeTiere: GruppeTier[], kontakt: Betriebskontakt | null): Observable<boolean> {
        if (!gruppeTiere?.length) {
            return of(false);
        }

        const updates = gruppeTiere.map(gt => {
            const tier = { ...gt };
            tier.herkunft_vvvo = kontakt?.vvvo ?? '';
            tier.herkunft_betrieb = kontakt ? kontakt.bezeichnung_final || kontakt.bezeichnung || '' : '';
            tier.herkunft_strasse = kontakt ? kontakt.strasse_final || kontakt.strasse || '' : '';
            tier.herkunft_plz = kontakt ? kontakt.plz_final || kontakt.plz || '' : '';
            tier.herkunft_ort = kontakt ? kontakt.ort_final || kontakt.ort || '' : '';
            tier.herkunft_ortsteil = kontakt?.ortsteil ?? '';
            return this.updateHerkunft(tier);
        });

        return forkJoin(updates).pipe(map(() => true));
    }

    /** Dashboard: tierliste kunde_id_abgang only (nur_tierliste), no animal updates. */
    updateTierlisteHerkunftOnly(tierlisteId: number, kontakt: Betriebskontakt | null): Observable<boolean> {
        const resolvedTierlisteId = Number(tierlisteId) || 0;
        if (resolvedTierlisteId <= 0) {
            return of(false);
        }

        const payload: Record<string, unknown> = {
            nur_tierliste: 1,
            tierliste_id: resolvedTierlisteId,
            gruppe_tiere: [],
            herkunft_vvvo: kontakt?.vvvo ?? '',
            herkunft_betrieb: kontakt ? kontakt.bezeichnung_final || kontakt.bezeichnung || '' : '',
            herkunft_strasse: kontakt ? kontakt.strasse_final || kontakt.strasse || '' : '',
            herkunft_plz: kontakt ? kontakt.plz_final || kontakt.plz || '' : '',
            herkunft_ort: kontakt ? kontakt.ort_final || kontakt.ort || '' : '',
            herkunft_ortsteil: kontakt?.ortsteil ?? '',
        };

        return this.api
            .post(this.apiBaseUrl + 'gruppe_tier/update_herkunft_mehrere.php', payload, null)
            .pipe(map(() => true));
    }

    createOrUpdateBemerkung(bemerkung: Tierbemerkung) {
        return this.api.updateGeneric(`${this.apiBaseUrl}gruppe_tier_bemerkung/update_kundenportal.php`, bemerkung);
    }

    deleteBemerkung(bemerkung: Tierbemerkung) {
        return this.api.deleteGeneric(this.apiBaseUrl + 'gruppe_tier_bemerkung/delete_kundenportal.php', bemerkung);
    }

    gruppeAendern(
        gruppeTiere: GruppeTier[],
        gruppe_id_alt: number,
        gruppe_id_neu: number,
        dashboardSync?: GruppeAendernDashboardSync,
    ) {
        const gt_ids = gruppeTiere
            .map(gt => Number(gt.gt_id))
            .filter(gtId => gtId > 0);
        const object: Record<string, unknown> = {
            gruppe_id: Number(gruppe_id_alt),
            gruppe_id_alt: Number(gruppe_id_alt),
            gruppe_id_neu: Number(gruppe_id_neu),
            gt_ids: gt_ids,
        };
        if (dashboardSync?.kunde_id && dashboardSync.kunde_id > 0) {
            object.kunde_id = dashboardSync.kunde_id;
        }
        if (dashboardSync?.gda_id && dashboardSync.gda_id > 0) {
            object.gda_id = dashboardSync.gda_id;
        }
        if (dashboardSync?.tierliste_id && dashboardSync.tierliste_id > 0) {
            object.tierliste_id = dashboardSync.tierliste_id;
        }
        if (dashboardSync?.einstall_tierliste_id && dashboardSync.einstall_tierliste_id > 0) {
            object.einstall_tierliste_id = dashboardSync.einstall_tierliste_id;
        }
        return this.api.updateGeneric(this.apiBaseUrl + 'gruppe_tier/update_gruppe.php', object);
    }

    ohrmarkeAlsGescanntMarkieren(gruppe_id: number, gruppeTiere: GruppeTier[]): Observable<boolean> {
        const subject = new Subject<boolean>();
        const input = new ConfirmDialogInput();
        input.headline = 'Ohrmarke(n) als gescannt markieren';
        input.text =
            'Wollen Sie die Ohrmarke(n) als gescannt markieren? Dieser Vorgang kann nicht rückgängig gemacht werden.';
        input.okButton = 'Markieren';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: input });
        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    let gt_ids = null;
                    if (gruppeTiere) {
                        gt_ids = [];
                        for (const gt of gruppeTiere) {
                            gt_ids.push(gt.gt_id);
                        }
                    }

                    const obj = { gruppe_id: gruppe_id, ohrmarken_gescannt: 1, gt_ids: gt_ids };
                    this.api.updateGeneric(this.apiBaseUrl + 'gruppe_tier/alle_ohrmarken_gescannt.php', obj).subscribe({
                        next: () => {
                            this.messageService.infoMessage('Alle Ohrmarken als gescannt markiert!');
                            subject.next(true);
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler!', err);
                            subject.next(false);
                        },
                    });
                } else {
                    subject.next(false);
                }
            },
        });

        return subject.asObservable();
    }

    /** Returns the new {@link Gruppe} on success, `null` on cancel or error. */
    openGruppeAuswaehlenDialog(
        gruppeTiere: GruppeTier[],
        dashboardSync?: GruppeAendernDashboardSync,
    ): Observable<Gruppe | null> {
        const subject = new Subject<Gruppe | null>();
        if (gruppeTiere?.length > 0) {
            // Prüfen ob alle Tiere aus derselben Gruppe
            const gruppe_id_alt = Number(gruppeTiere[0].gruppe_id);
            for (const tier of gruppeTiere) {
                if (!KieneUtils.sameId(tier.gruppe_id, gruppe_id_alt)) {
                    subject.next(null);
                    return subject.asObservable();
                }
            }

            this.gruppenService.getGruppeKundenportal(gruppe_id_alt).subscribe({
                next: (gruppe: Gruppe) => {
                    const input = new GruppeAuswaehlenDialogInput();
                    input.kunde_id = gruppe.kunde_id;
                    input.except_gruppe_id = gruppe_id_alt;
                    this.dialog
                        .open<
                            GruppeAuswaehlenDialogComponent,
                            GruppeAuswaehlenDialogInput,
                            GruppeAuswaehlenDialogResult
                        >(
                            GruppeAuswaehlenDialogComponent,
                            buildGruppeAuswaehlenDialogConfig(input, {
                                mobile: this.appService.isMobileScreen,
                            }),
                        )
                        .afterClosed()
                        .subscribe({
                            next: (result2: GruppeAuswaehlenDialogResult) => {
                                if (result2?.confirmed) {
                                    this.gruppeAendern(
                                        gruppeTiere,
                                        gruppe_id_alt,
                                        Number(result2?.gruppe?.gruppe_id),
                                        dashboardSync,
                                    ).subscribe({
                                        next: () => {
                                            this.messageService
                                                .infoMessageAction(
                                                    'Gruppe erfolgreich geändert!',
                                                    'zur Gruppe springen',
                                                )
                                                .subscribe({
                                                    next: () => {
                                                        this.router
                                                            .navigate([
                                                                'gruppen',
                                                                'aktuelle',
                                                                result2?.gruppe?.gruppe_id,
                                                            ])
                                                            .then();
                                                    },
                                                });

                                            subject.next(result2.gruppe);
                                        },
                                        error: err => {
                                            this.messageService.errorMessage(
                                                'Gruppe konnte nicht geändert werden!',
                                                err,
                                            );
                                            subject.next(null);
                                        },
                                    });
                                } else {
                                    subject.next(null);
                                }
                            },
                        });
                },
                error: err => {
                    this.messageService.errorMessage('Fehler!', err);
                    subject.next(null);
                },
            });
        } else {
            subject.next(null);
        }
        return subject.asObservable();
    }

    abgangStornieren(gruppeTier: GruppeTier) {
        return this.api.post(this.apiBaseUrl + 'gruppe_tier/abgang_stornieren.php', gruppeTier, null);
    }

    updateHerkunftMultiple(
        gruppeTiere: GruppeTier[],
        kontakt: Betriebskontakt | null,
        tierlisteId?: number | null,
    ): Observable<boolean> {
        if (!gruppeTiere?.length) {
            return of(false);
        }

        const payload: Record<string, unknown> = {
            gruppe_tiere: gruppeTiere.map(gt => ({ gt_id: gt.gt_id })),
            herkunft_vvvo: kontakt?.vvvo ?? '',
            herkunft_betrieb: kontakt ? kontakt.bezeichnung_final || kontakt.bezeichnung || '' : '',
            herkunft_strasse: kontakt ? kontakt.strasse_final || kontakt.strasse || '' : '',
            herkunft_plz: kontakt ? kontakt.plz_final || kontakt.plz || '' : '',
            herkunft_ort: kontakt ? kontakt.ort_final || kontakt.ort || '' : '',
            herkunft_ortsteil: kontakt?.ortsteil ?? '',
        };
        const resolvedTierlisteId = Number(tierlisteId) || 0;
        if (resolvedTierlisteId > 0) {
            payload.tierliste_id = resolvedTierlisteId;
        }

        return this.api
            .post(this.apiBaseUrl + 'gruppe_tier/update_herkunft_mehrere.php', payload, null)
            .pipe(map(() => true));
    }

    private resolveGruppeIdForBulkUpdate(gruppeTiere: GruppeTier[], gruppeId?: number): number {
        const explicit = Number(gruppeId);
        if (explicit > 0) {
            return explicit;
        }
        for (const tier of gruppeTiere ?? []) {
            const fromTier = Number(tier?.gruppe_id);
            if (fromTier > 0) {
                return fromTier;
            }
        }
        return 0;
    }

    updateReklamationenMultiple(
        gruppeTiere: GruppeTier[],
        reklamationIds: number[],
        gruppeId?: number,
    ): Observable<boolean> {
        if (!gruppeTiere?.length) {
            return of(false);
        }

        const payload: Record<string, unknown> = {
            gruppe_tiere: gruppeTiere.map(gt => ({ gt_id: gt.gt_id })),
            reklamationen: (reklamationIds ?? []).map(id => ({ tierreklamation_id: id })),
        };
        const resolvedGruppeId = this.resolveGruppeIdForBulkUpdate(gruppeTiere, gruppeId);
        if (resolvedGruppeId > 0) {
            payload.gruppe_id = resolvedGruppeId;
        }

        return this.api.post(this.apiBaseUrl + 'gruppe_tier/update_reklamation.php', payload, null).pipe(map(() => true));
    }

    updateKontaktMultiple(
        gruppeTiere: GruppeTier[],
        options: {
            kontaktId?: number | null;
            removeViehhaendler?: boolean;
            updateKontakt?: boolean;
            updateViehhaendler?: boolean;
            viehhaendlerId?: number | null;
            gruppeId?: number;
        } = {},
    ): Observable<boolean> {
        if (!gruppeTiere?.length) {
            return of(false);
        }

        const shouldUpdateKontakt =
            options?.updateKontakt ?? Object.prototype.hasOwnProperty.call(options, 'kontaktId');
        const shouldUpdateViehhaendler =
            options?.updateViehhaendler ?? Object.prototype.hasOwnProperty.call(options, 'viehhaendlerId');

        const payload: any = {
            gruppe_tiere: gruppeTiere.map(gt => ({ gt_id: gt.gt_id })),
            update_kontakt: shouldUpdateKontakt ? 1 : 0,
            remove_viehhaendler:
                shouldUpdateKontakt && (options?.removeViehhaendler ?? true) ? 1 : 0,
            update_viehhaendler: shouldUpdateViehhaendler ? 1 : 0,
        };
        const resolvedGruppeId = this.resolveGruppeIdForBulkUpdate(gruppeTiere, options?.gruppeId);
        if (resolvedGruppeId > 0) {
            payload.gruppe_id = resolvedGruppeId;
        }

        if (shouldUpdateKontakt) {
            payload.betriebskontakt_haendler_id = Object.prototype.hasOwnProperty.call(options, 'kontaktId')
                ? options?.kontaktId
                : null;
        }

        if (shouldUpdateViehhaendler) {
            payload.viehhaendler_id = Object.prototype.hasOwnProperty.call(options, 'viehhaendlerId')
                ? options?.viehhaendlerId
                : null;
        }

        return this.api.post(this.apiBaseUrl + 'gruppe_tier/update_kontakte.php', payload, null).pipe(map(() => true));
    }

    updateEinstallgewichteMultiple(gruppeId: number, gruppeTiere: GruppeTier[], einstallgewicht: number): Observable<boolean> {
        if (!gruppeTiere?.length || Number(gruppeId) <= 0 || !(Number(einstallgewicht) > 0)) {
            return of(false);
        }

        const payload = {
            gruppe_id: Number(gruppeId),
            records: gruppeTiere.map(gt => ({
                gt_id: Number(gt.gt_id),
                einstallgewicht: Number(einstallgewicht),
            })),
        };

        return this.api.post(this.apiBaseUrl + 'gruppe_standort/save_einstallgewichte.php', payload, null).pipe(
            map(() => true),
        );
    }

    updateAusstallgewichteMultiple(gruppeId: number, gruppeTiere: GruppeTier[], ausstallgewicht: number): Observable<boolean> {
        if (!gruppeTiere?.length || Number(gruppeId) <= 0 || !(Number(ausstallgewicht) > 0)) {
            return of(false);
        }

        const payload = {
            gruppe_id: Number(gruppeId),
            records: gruppeTiere.map(gt => ({
                gt_id: Number(gt.gt_id),
                most_actual_abgangsgewicht: Number(ausstallgewicht),
            })),
        };

        return this.api
            .post(this.apiBaseUrl + 'gruppe_standort/save_most_actual_abgangsgewichte.php', payload, null)
            .pipe(map(() => true));
    }

    /** Einstall then Ausstall sequentially (avoids parallel DB errors on first save). */
    saveGewichteFestlegen(
        gruppeId: number,
        gruppeTiere: GruppeTier[],
        einstallgewicht?: number | null,
        ausstallgewicht?: number | null,
    ): Observable<boolean> {
        const steps: Observable<boolean>[] = [];
        if (einstallgewicht !== null && einstallgewicht !== undefined && Number(einstallgewicht) > 0) {
            steps.push(this.updateEinstallgewichteMultiple(gruppeId, gruppeTiere, Number(einstallgewicht)));
        }
        if (ausstallgewicht !== null && ausstallgewicht !== undefined && Number(ausstallgewicht) > 0) {
            steps.push(this.updateAusstallgewichteMultiple(gruppeId, gruppeTiere, Number(ausstallgewicht)));
        }
        if (steps.length === 0) {
            return of(false);
        }
        return concat(...steps).pipe(last());
    }

    aussendienstSetzen(gruppeTiere: GruppeTier[]): Observable<boolean> {
        const subject = new Subject<boolean>();

        if (gruppeTiere?.length > 0) {
            const input = new GruppeTierAussendienstDialogInput();
            input.gruppeTiere = gruppeTiere;
            input.anzahlTiere = gruppeTiere.length;

            const dialogRef = this.dialog.open(GruppeTierAussendienstDialogComponent, { data: input, width: '500px' });

            dialogRef.afterClosed().subscribe({
                next: (result: boolean) => {
                    subject.next(result);
                },
                error: err => {
                    this.messageService.errorMessage('Fehler beim Öffnen des Dialogs!', err);
                    subject.next(false);
                },
            });
        } else {
            this.messageService.errorMessage('Keine Tiere ausgewählt!', null);
            subject.next(false);
        }

        return subject.asObservable();
    }

    /*(gruppeTiere: GruppeTier[]): Observable<number> {
        const subject = new Subject<number>();
        if (gruppeTiere?.length > 0) {
            // Prüfen ob alle Tiere aus derselben Gruppe
            const gruppe_id_alt = gruppeTiere[0].gruppe_id;
            for (const tier of gruppeTiere) {
                if (tier.gruppe_id !== gruppe_id_alt) {
                    subject.next(undefined);
                    return subject.asObservable();
                }
            }

            this.gruppenService.getGruppeKundenportal(gruppe_id_alt).subscribe({
                next: (gruppe: Gruppe) => {
                    const input = new GruppeAuswaehlenDialogInput();
                    input.kunde_id = gruppe.kunde_id;
                    input.except_gruppe_id = gruppe_id_alt;
                    this.dialog
                        .open<
                            GruppeAuswaehlenDialogComponent,
                            GruppeAuswaehlenDialogInput,
                            GruppeAuswaehlenDialogResult
                        >(
                            GruppeAuswaehlenDialogComponent,
                            buildGruppeAuswaehlenDialogConfig(input, {
                                mobile: this.appService.isMobileScreen,
                            }),
                        )
                        .afterClosed()
                        .subscribe({
                            next: (result2: GruppeAuswaehlenDialogResult) => {
                                if (result2?.confirmed) {
                                    const stugi = new SelektierteTiereUpdateGruppeInput();
                                    stugi.gruppe_id_neu = result2.gruppe.gruppe_id;
                                    stugi.gruppe_id_alt = gruppe_id_alt;
                                    stugi.gt_ids = [];
                                    for (const gt of gruppeTiere) {
                                        stugi.gt_ids.push(gt.gt_id);
                                    }

                                    this.api
                                        .post(
                                            this.apiBaseUrl + 'gruppe_tier/selektierte_tiere_update_gruppe.php',
                                            stugi,
                                        )
                                        .subscribe({
                                            next: () => {
                                                subject.next(result2.gruppe.gruppe_id);
                                            },
                                            error: (err) => {
                                                this.messageService.errorMessage('Fehler!', err);
                                                subject.next(undefined);
                                            },
                                        });
                                } else {
                                    subject.next(undefined);
                                }
                            },
                        });
                },
                error: (err) => {
                    this.messageService.errorMessage('Fehler!', err);
                    subject.next(undefined);
                },
            });
        } else {
            subject.next(undefined);
        }
        return subject.asObservable();
    }*/
}
